import { Injectable, inject, DestroyRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  LOCALE_LABELS,
  LOCALE_EN,
  type Locale,
} from '../constants/locale.constants';
import { AppState } from '../store/app.state';
import { selectTenantPlatformLanguage } from '../store/tenant/tenant.selectors';

@Injectable({ providedIn: 'root' })
export class I18nService {
  private translate = inject(TranslateService);
  private router = inject(Router);
  private store = inject(Store<AppState>);
  private destroyRef = inject(DestroyRef);
  private readonly STORAGE_KEY = 'app_language';
  private readonly DEFAULT_LANGUAGE = DEFAULT_LOCALE;

  init() {
    this.translate.addLangs([...SUPPORTED_LOCALES]);
    this.applyLanguageFromContext();
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.applyLanguageFromContext());
    this.store
      .select(selectTenantPlatformLanguage)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.applyLanguageFromContext());
  }

  private applyLanguageFromContext(): void {
    const url = this.router.url.split('?')[0];
    if (url === '/login' || url.startsWith('/admin')) {
      this.translate.use(LOCALE_EN);
      document.documentElement.lang = LOCALE_EN;
      return;
    }
    // Invite-accept page: language is set by the component after validation (tenant platformLanguage)
    if (url.startsWith('/invite/accept')) {
      return;
    }
    this.store
      .select(selectTenantPlatformLanguage)
      .pipe(take(1))
      .subscribe((tenantLang) => {
        if (tenantLang) {
          this.translate.use(tenantLang);
          document.documentElement.lang = tenantLang;
          localStorage.setItem(this.STORAGE_KEY, tenantLang);
          return;
        }
        const savedLang = localStorage.getItem(this.STORAGE_KEY);
        const browserLang = this.translate.getBrowserLang();
        const language =
          savedLang && SUPPORTED_LOCALES.includes(savedLang as Locale)
            ? savedLang
            : browserLang && SUPPORTED_LOCALES.includes(browserLang as Locale)
              ? browserLang
              : this.DEFAULT_LANGUAGE;
        this.setLanguage(language);
      });
  }

  setLanguage(lang: string) {
    if (SUPPORTED_LOCALES.includes(lang as Locale)) {
      this.translate.use(lang);
      localStorage.setItem(this.STORAGE_KEY, lang);
      document.documentElement.lang = lang;
    }
  }

  getCurrentLanguage(): string {
    return this.translate.currentLang || this.DEFAULT_LANGUAGE;
  }

  getAvailableLanguages(): string[] {
    return [...SUPPORTED_LOCALES];
  }

  getLanguageName(code: string): string {
    return LOCALE_LABELS[code as Locale] ?? code;
  }
}
